import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import useBreakpoint from '../hooks/useBreakpoint';
import BottomSheet from './BottomSheet';
import { kpiGridMobile, pageMobile, TAP_TARGET } from '../mobileStyles';

// Lima is a fixed UTC-5 offset (no DST), same convention as api/_lib/summarize.js.
const LIMA_OFFSET_MS = 5 * 60 * 60 * 1000;
const REFRESH_MS = 2 * 60 * 1000;

const CATEGORIAS = ['CALIFICADO', 'POTENCIAL', 'DESCALIFICADO'];

const CATEGORIA_COLOR = {
  CALIFICADO:    { bg: 'rgba(74,124,89,0.12)',   fg: '#4A7C59', solid: '#4A7C59' },
  POTENCIAL:     { bg: 'rgba(201,169,110,0.18)', fg: '#8A6D2F', solid: '#C9A96E' },
  DESCALIFICADO: { bg: 'rgba(155,155,155,0.16)', fg: '#6B6B6B', solid: '#9B9B9B' },
};
function categoriaColor(c) {
  return CATEGORIA_COLOR[c] || { bg: 'rgba(155,155,155,0.12)', fg: '#9B9B9B', solid: '#9B9B9B' };
}

// Every column that exists on the leads table. Keys found inside `respuestas`
// that are NOT here become dynamic filters + a "Respuestas adicionales" block,
// so a new question in the form is filterable with zero code changes.
const TABLE_COLUMNS = new Set([
  'id', 'nombre', 'whatsapp', 'email', 'dni', 'direccion', 'distrito',
  'tipo_espacio', 'estado_inmueble', 'metros_cuadrados', 'ambientes', 'ambientes_otro',
  'servicio', 'presupuesto', 'cuando_inicia', 'dudas',
  'categoria', 'distrito_ok', 'presupuesto_ok', 'tiempo_ok',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'respuestas', 'atendido', 'notas', 'proyecto_id', 'fecha_envio', 'created_at',
]);

// ── helpers ───────────────────────────────────────────────────────────────
function limaParts(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Shift the instant so the UTC getters read Lima wall-clock time.
  return new Date(d.getTime() - LIMA_OFFSET_MS);
}
function fmtFechaLima(value) {
  const d = limaParts(value);
  if (!d) return '—';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
// Real UTC instant of this week's Monday 00:00 in Lima.
function limaWeekStartMs() {
  const shifted = new Date(Date.now() - LIMA_OFFSET_MS);
  const dow = (shifted.getUTCDay() + 6) % 7; // Monday = 0
  const monday = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate() - dow);
  return monday + LIMA_OFFSET_MS;
}

// Peru numbers are stored in all shapes ("999 888 777", "+51999888777"). Keep
// the digits, assume +51 for the local 9-digit mobile format.
function waLink(whatsapp) {
  const digits = String(whatsapp || '').replace(/\D/g, '');
  if (!digits) return null;
  const full = digits.length === 9 && digits.startsWith('9') ? `51${digits}` : digits;
  return `https://wa.me/${full}`;
}

function textOf(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.filter(x => x != null && String(x).trim()).join(', ');
  if (typeof v === 'object') return '';
  return String(v).trim();
}

// Distinct non-empty values for a getter, alphabetical.
function distinctValues(rows, getter) {
  const set = new Set();
  for (const r of rows) {
    const raw = getter(r);
    const values = Array.isArray(raw) ? raw : [raw];
    for (const v of values) {
      const s = textOf(v);
      if (s) set.add(s);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'es'));
}

// Does a lead's respuestas hold this key/value pair? Arrays match by inclusion.
function respuestaMatches(lead, key, value) {
  const raw = lead.respuestas?.[key];
  if (raw == null) return false;
  if (Array.isArray(raw)) return raw.some(v => textOf(v) === value);
  return textOf(raw) === value;
}

// ── icons ─────────────────────────────────────────────────────────────────
function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 10a7 7 0 01-12 4.9M3 10a7 7 0 0112-4.9" />
      <path d="M15 2v3.5h-3.5M5 18v-3.5h3.5" />
    </svg>
  );
}
function ChevronIcon({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>
      <path d="M7 4l6 6-6 6" />
    </svg>
  );
}

// Breakpoint flags shared with the sub-components below (desktop: all false).
const LeadsBp = React.createContext({ isMobile: false, isCompact: false });

// ── component ─────────────────────────────────────────────────────────────
export default function Leads({ onNavigate } = {}) {
  const bp = useBreakpoint();
  const { isMobile, isCompact } = bp;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [categoria, setCategoria] = useState('all');
  const [estado, setEstado] = useState('all'); // 'all' | 'pendientes' | 'atendidos'
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({}); // column name → selected value
  const [extraFilters, setExtraFilters] = useState({}); // respuestas key → selected value

  // Notas drafts, keyed by lead id, so typing doesn't fight the auto-refresh.
  const [notasDraft, setNotasDraft] = useState({});
  const notasDraftRef = useRef(notasDraft);
  notasDraftRef.current = notasDraft;

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      setLoadError(error.message);
    } else {
      setLoadError(null);
      setLeads(data || []);
      setLastUpdated(new Date());
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 2 minutes.
  useEffect(() => {
    const id = setInterval(() => { load(); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // ── dynamic filter definitions ──────────────────────────────────────────
  // Every dropdown's options come from the values actually present in the
  // loaded rows — nothing here is hardcoded.
  const columnFilterDefs = useMemo(() => ([
    { key: 'distrito',      label: 'Distrito' },
    { key: 'presupuesto',   label: 'Presupuesto' },
    { key: 'servicio',      label: 'Servicio' },
    { key: 'tipo_espacio',  label: 'Tipo de espacio' },
    { key: 'cuando_inicia', label: 'Cuándo inicia' },
    { key: 'utm_source',    label: 'Origen' },
    { key: 'utm_campaign',  label: 'Campaña' },
  ].map(def => ({ ...def, options: distinctValues(leads, l => l[def.key]) }))), [leads]);

  // Keys inside respuestas that aren't table columns → one dropdown each.
  const extraFilterDefs = useMemo(() => {
    const keys = new Set();
    for (const l of leads) {
      const r = l.respuestas;
      if (!r || typeof r !== 'object' || Array.isArray(r)) continue;
      for (const k of Object.keys(r)) {
        if (!TABLE_COLUMNS.has(k)) keys.add(k);
      }
    }
    return [...keys]
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map(key => ({
        key,
        label: key.replace(/[_-]+/g, ' ').replace(/^\w/, c => c.toUpperCase()),
        options: distinctValues(leads, l => l.respuestas?.[key]),
      }))
      .filter(def => def.options.length > 0);
  }, [leads]);

  // ── filtering ───────────────────────────────────────────────────────────
  // Everything except the categoría pills, so the pill counts reflect the
  // rest of the active filters.
  const filteredExceptCategoria = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter(l => {
      if (estado === 'pendientes' && l.atendido) return false;
      if (estado === 'atendidos' && !l.atendido) return false;

      for (const [key, value] of Object.entries(colFilters)) {
        if (!value) continue;
        const raw = l[key];
        const ok = Array.isArray(raw)
          ? raw.some(v => textOf(v) === value)
          : textOf(raw) === value;
        if (!ok) return false;
      }

      for (const [key, value] of Object.entries(extraFilters)) {
        if (!value) continue;
        if (!respuestaMatches(l, key, value)) return false;
      }

      if (q) {
        const haystack = [l.nombre, l.whatsapp, l.email, l.dni]
          .map(v => String(v || '').toLowerCase())
          .join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [leads, estado, colFilters, extraFilters, search]);

  const categoriaCounts = useMemo(() => {
    const counts = { all: filteredExceptCategoria.length };
    for (const c of CATEGORIAS) counts[c] = 0;
    for (const l of filteredExceptCategoria) {
      if (counts[l.categoria] != null) counts[l.categoria] += 1;
    }
    return counts;
  }, [filteredExceptCategoria]);

  const visibleLeads = useMemo(() => (
    categoria === 'all'
      ? filteredExceptCategoria
      : filteredExceptCategoria.filter(l => l.categoria === categoria)
  ), [filteredExceptCategoria, categoria]);

  // ── KPIs (over all loaded leads, not the filtered view) ──────────────────
  const kpis = useMemo(() => {
    const total = leads.length;
    const calificados = leads.filter(l => l.categoria === 'CALIFICADO').length;
    const pendientes = leads.filter(l => !l.atendido).length;
    const weekStart = limaWeekStartMs();
    const semana = leads.filter(l => {
      const t = new Date(l.created_at || l.fecha_envio || 0).getTime();
      return Number.isFinite(t) && t >= weekStart;
    }).length;
    return {
      total,
      calificados,
      calificadosPct: total ? Math.round((calificados / total) * 100) : 0,
      pendientes,
      semana,
    };
  }, [leads]);

  const activeFilterCount =
    (categoria !== 'all' ? 1 : 0) +
    (estado !== 'all' ? 1 : 0) +
    (search.trim() ? 1 : 0) +
    Object.values(colFilters).filter(Boolean).length +
    Object.values(extraFilters).filter(Boolean).length;

  const sheetFilterCount =
    (estado !== 'all' ? 1 : 0) +
    Object.values(colFilters).filter(Boolean).length +
    Object.values(extraFilters).filter(Boolean).length;

  function clearFilters() {
    setCategoria('all');
    setEstado('all');
    setSearch('');
    setColFilters({});
    setExtraFilters({});
  }

  // ── writes ──────────────────────────────────────────────────────────────
  async function toggleAtendido(lead) {
    const next = !lead.atendido;
    setLeads(prev => prev.map(l => (l.id === lead.id ? { ...l, atendido: next } : l)));
    const { error } = await supabase.from('leads').update({ atendido: next }).eq('id', lead.id);
    if (error) {
      setLeads(prev => prev.map(l => (l.id === lead.id ? { ...l, atendido: !next } : l)));
      alert('Error al actualizar: ' + error.message);
    }
  }

  async function saveNotas(lead) {
    const draft = notasDraftRef.current[lead.id];
    if (draft == null || draft === (lead.notas || '')) return;
    const value = draft.trim() ? draft : null;
    setLeads(prev => prev.map(l => (l.id === lead.id ? { ...l, notas: value } : l)));
    const { error } = await supabase.from('leads').update({ notas: value }).eq('id', lead.id);
    if (error) alert('Error al guardar la nota: ' + error.message);
  }

  function crearProyecto(lead) {
    if (!onNavigate) return;
    onNavigate('dashboard', {
      leadPrefill: {
        cliente: lead.nombre || '',
        cliente_telefono: lead.whatsapp || '',
        cliente_email: lead.email || '',
        cliente_dni: lead.dni || '',
        cliente_origen: lead.utm_source || '',
      },
    });
  }

  // ── render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={isMobile ? { ...s.page, ...pageMobile } : s.page}>
        <h1 style={s.title}>Leads</h1>
        <div style={s.loading}>Cargando leads…</div>
      </div>
    );
  }

  return (
    <LeadsBp.Provider value={bp}>
    <div style={isMobile ? { ...s.page, ...pageMobile } : s.page}>
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Leads</h1>
          <p style={s.subtitle}>
            Formularios recibidos, calificación automática y atribución de campañas
          </p>
        </div>
        <div style={s.headerActions}>
          {lastUpdated && (
            <span style={s.updatedAt}>Actualizado {fmtFechaLima(lastUpdated)}</span>
          )}
          <button style={isCompact ? { ...s.refreshBtn, ...s.tapMobile } : s.refreshBtn} onClick={load}>
            <RefreshIcon /> Actualizar
          </button>
        </div>
      </div>

      {loadError && (
        <div style={s.errorBox}>
          No se pudieron cargar los leads: {loadError}
          <div style={s.errorHint}>
            Si la tabla aún no existe, corre <code>supabase-leads-migration.sql</code> en el SQL Editor de Supabase.
          </div>
        </div>
      )}

      <div style={isMobile ? { ...s.kpiRow, ...kpiGridMobile } : s.kpiRow}>
        <Kpi label="Total leads" value={kpis.total} />
        <Kpi
          label="Calificados"
          value={kpis.calificados}
          hint={`${kpis.calificadosPct}% del total`}
          color="#4A7C59"
        />
        <Kpi label="Pendientes de atender" value={kpis.pendientes} color="#C4922A" />
        <Kpi label="Leads esta semana" value={kpis.semana} color="var(--wine)" />
      </div>

      {isMobile ? (
        <div style={s.filterCard}>
          <div style={{ ...s.pillRow, ...s.pillRowMobile }}>
            <Pill active={categoria === 'all'} onClick={() => setCategoria('all')} count={categoriaCounts.all}>
              Todos
            </Pill>
            {CATEGORIAS.map(c => (
              <Pill
                key={c}
                active={categoria === c}
                onClick={() => setCategoria(c)}
                count={categoriaCounts[c]}
                color={categoriaColor(c).solid}
              >
                {c.charAt(0) + c.slice(1).toLowerCase()}
              </Pill>
            ))}
          </div>

          <div style={s.searchRowMobile}>
            <input
              type="search"
              style={{ ...s.select, ...s.inputMobile, flex: 1 }}
              placeholder="Nombre, WhatsApp, email o DNI"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Buscar"
            />
            <button
              type="button"
              style={{ ...s.clearBtn, ...s.tapMobile, ...s.filtersBtnMobile, ...(sheetFilterCount > 0 ? s.filtersBtnActive : {}) }}
              onClick={() => setFiltersOpen(true)}
            >
              Filtros{sheetFilterCount > 0 ? ` (${sheetFilterCount})` : ''}
            </button>
          </div>

          <div style={s.filterFooter}>
            <span style={s.resultCount}>
              {visibleLeads.length} {visibleLeads.length === 1 ? 'lead' : 'leads'}
              {activeFilterCount > 0 ? ' · filtros activos' : ''}
            </span>
            {activeFilterCount > 0 && (
              <button style={{ ...s.clearBtn, ...s.tapMobile }} onClick={clearFilters}>Limpiar filtros</button>
            )}
          </div>

          <BottomSheet
            open={filtersOpen}
            onClose={closeFilters}
            title="Filtros"
            footer={(
              <>
                <button
                  type="button"
                  style={{ ...s.clearBtn, ...s.tapMobile, flex: 1 }}
                  onClick={() => { setEstado('all'); setColFilters({}); setExtraFilters({}); }}
                >
                  Limpiar
                </button>
                <button
                  type="button"
                  style={{ ...s.actionBtn, ...s.projectBtn, ...s.tapMobile, flex: 1, justifyContent: 'center' }}
                  onClick={closeFilters}
                >
                  Ver {visibleLeads.length} {visibleLeads.length === 1 ? 'lead' : 'leads'}
                </button>
              </>
            )}
          >
            <div style={s.field}>
              <span style={s.fieldLabel}>Estado</span>
              <select style={{ ...s.select, ...s.inputMobile }} value={estado} onChange={e => setEstado(e.target.value)}>
                <option value="all">Todos</option>
                <option value="pendientes">Pendientes</option>
                <option value="atendidos">Atendidos</option>
              </select>
            </div>
            {columnFilterDefs.map(def => (
              <Select
                key={def.key}
                label={def.label}
                value={colFilters[def.key] || ''}
                options={def.options}
                onChange={v => setColFilters(prev => ({ ...prev, [def.key]: v }))}
              />
            ))}
            {extraFilterDefs.map(def => (
              <Select
                key={`extra:${def.key}`}
                label={def.label}
                value={extraFilters[def.key] || ''}
                options={def.options}
                onChange={v => setExtraFilters(prev => ({ ...prev, [def.key]: v }))}
              />
            ))}
          </BottomSheet>
        </div>
      ) : (
      <div style={s.filterCard}>
        <div style={s.pillRow}>
          <Pill active={categoria === 'all'} onClick={() => setCategoria('all')} count={categoriaCounts.all}>
            Todos
          </Pill>
          {CATEGORIAS.map(c => (
            <Pill
              key={c}
              active={categoria === c}
              onClick={() => setCategoria(c)}
              count={categoriaCounts[c]}
              color={categoriaColor(c).solid}
            >
              {c.charAt(0) + c.slice(1).toLowerCase()}
            </Pill>
          ))}
        </div>

        <div style={s.filterGrid}>
          {columnFilterDefs.map(def => (
            <Select
              key={def.key}
              label={def.label}
              value={colFilters[def.key] || ''}
              options={def.options}
              onChange={v => setColFilters(prev => ({ ...prev, [def.key]: v }))}
            />
          ))}
          {extraFilterDefs.map(def => (
            <Select
              key={`extra:${def.key}`}
              label={def.label}
              value={extraFilters[def.key] || ''}
              options={def.options}
              onChange={v => setExtraFilters(prev => ({ ...prev, [def.key]: v }))}
            />
          ))}
          <div style={s.field}>
            <span style={s.fieldLabel}>Estado</span>
            <select style={s.select} value={estado} onChange={e => setEstado(e.target.value)}>
              <option value="all">Todos</option>
              <option value="pendientes">Pendientes</option>
              <option value="atendidos">Atendidos</option>
            </select>
          </div>
          <div style={{ ...s.field, minWidth: 210 }}>
            <span style={s.fieldLabel}>Buscar</span>
            <input
              type="text"
              style={s.select}
              placeholder="Nombre, WhatsApp, email o DNI"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div style={s.filterFooter}>
          <span style={s.resultCount}>
            {visibleLeads.length} {visibleLeads.length === 1 ? 'lead' : 'leads'}
            {activeFilterCount > 0 ? ' · filtros activos' : ''}
          </span>
          {activeFilterCount > 0 && (
            <button style={s.clearBtn} onClick={clearFilters}>Limpiar filtros</button>
          )}
        </div>
      </div>
      )}

      {visibleLeads.length === 0 ? (
        <div style={s.empty}>
          {leads.length === 0
            ? 'Aún no hay leads registrados.'
            : 'Ningún lead coincide con los filtros seleccionados.'}
        </div>
      ) : (
        <div style={s.list}>
          {visibleLeads.map(lead => (
            <LeadRow
              key={lead.id}
              lead={lead}
              expanded={expandedId === lead.id}
              onToggle={() => setExpandedId(cur => (cur === lead.id ? null : lead.id))}
              onToggleAtendido={() => toggleAtendido(lead)}
              notasValue={notasDraft[lead.id] ?? (lead.notas || '')}
              onNotasChange={v => setNotasDraft(prev => ({ ...prev, [lead.id]: v }))}
              onNotasBlur={() => saveNotas(lead)}
              onCrearProyecto={() => crearProyecto(lead)}
            />
          ))}
        </div>
      )}
    </div>
    </LeadsBp.Provider>
  );
}

// ── sub-components ────────────────────────────────────────────────────────
function Kpi({ label, value, hint, color }) {
  return (
    <div style={s.kpiCard}>
      <span style={s.kpiLabel}>{label}</span>
      <span style={{ ...s.kpiValue, ...(color ? { color } : {}) }}>{value}</span>
      {hint && <span style={s.kpiHint}>{hint}</span>}
    </div>
  );
}

function Pill({ active, onClick, count, color, children }) {
  const { isMobile, isCompact } = React.useContext(LeadsBp);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...s.pill,
        ...(isCompact ? s.tapMobile : {}),
        ...(isMobile ? s.pillMobile : {}),
        ...(active
          ? { background: color || 'var(--wine)', color: '#fff', borderColor: 'transparent' }
          : {}),
      }}
    >
      {children}
      <span style={{ ...s.pillCount, ...(active ? s.pillCountActive : {}) }}>{count ?? 0}</span>
    </button>
  );
}

function Select({ label, value, options, onChange }) {
  const { isCompact } = React.useContext(LeadsBp);
  return (
    <div style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      <select style={isCompact ? { ...s.select, ...s.inputMobile } : s.select} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Todos</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Check({ ok, label }) {
  const good = ok === true;
  const bad = ok === false;
  return (
    <span style={{
      ...s.check,
      background: good ? 'rgba(74,124,89,0.12)' : bad ? 'rgba(184,59,59,0.10)' : 'rgba(155,155,155,0.12)',
      color: good ? '#4A7C59' : bad ? '#B83B3B' : '#9B9B9B',
    }}>
      {good ? '✓' : bad ? '✕' : '—'} {label}
    </span>
  );
}

function Row({ label, value }) {
  const { isMobile } = React.useContext(LeadsBp);
  const text = textOf(value);
  if (!text) return null;
  return (
    <div style={isMobile ? { ...s.detailRow, ...s.detailRowMobile } : s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      <span style={s.detailValue}>{text}</span>
    </div>
  );
}

function LeadRow({
  lead, expanded, onToggle, onToggleAtendido,
  notasValue, onNotasChange, onNotasBlur, onCrearProyecto,
}) {
  const { isMobile, isCompact } = React.useContext(LeadsBp);
  const cat = categoriaColor(lead.categoria);
  const wa = waLink(lead.whatsapp);
  const fecha = lead.fecha_envio || lead.created_at;

  // Anything in respuestas that isn't already a column of its own.
  const extras = useMemo(() => {
    const r = lead.respuestas;
    if (!r || typeof r !== 'object' || Array.isArray(r)) return [];
    return Object.entries(r).filter(([k]) => !TABLE_COLUMNS.has(k));
  }, [lead.respuestas]);

  return (
    <div style={{ ...s.card, ...(expanded ? s.cardExpanded : {}) }}>
      {isMobile ? (
        <div
          style={{ ...s.cardHead, ...s.cardHeadMobile }}
          onClick={onToggle}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        >
          <div style={s.mHeadTop}>
            <span style={s.chevron}><ChevronIcon open={expanded} /></span>
            <span style={{ ...s.leadName, ...s.leadNameMobile }}>{lead.nombre || 'Sin nombre'}</span>
          </div>
          <div style={s.headTop}>
            <span style={{ ...s.badge, ...s.badgeMobile, background: cat.bg, color: cat.fg }}>
              {lead.categoria || 'SIN CLASIFICAR'}
            </span>
            {lead.atendido && <span style={{ ...s.atendidoBadge, ...s.badgeMobile }}>✓ Atendido</span>}
          </div>
          <div style={s.mPairs}>
            <MobilePair label="WhatsApp" value={lead.whatsapp} />
            <MobilePair label="Distrito" value={lead.distrito} />
            <MobilePair label="Presupuesto" value={lead.presupuesto} />
            <MobilePair label="Servicio" value={lead.servicio} />
            <MobilePair label="Origen" value={lead.utm_source} />
            <MobilePair label="Campaña" value={lead.utm_campaign} />
            <MobilePair label="Fecha" value={fmtFechaLima(fecha)} />
          </div>
        </div>
      ) : (
      <div
        style={s.cardHead}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <span style={s.chevron}><ChevronIcon open={expanded} /></span>
        <div style={s.headMain}>
          <div style={s.headTop}>
            <span style={s.leadName}>{lead.nombre || 'Sin nombre'}</span>
            <span style={{ ...s.badge, background: cat.bg, color: cat.fg }}>
              {lead.categoria || 'SIN CLASIFICAR'}
            </span>
            {lead.atendido && <span style={s.atendidoBadge}>✓ Atendido</span>}
          </div>
          <div style={s.headMeta}>
            {lead.whatsapp && <span style={s.metaItem}>{lead.whatsapp}</span>}
            {lead.distrito && <span style={s.metaItem}>{lead.distrito}</span>}
            {lead.presupuesto && <span style={s.metaItem}>{lead.presupuesto}</span>}
            {lead.servicio && <span style={s.metaItem}>{lead.servicio}</span>}
          </div>
        </div>
        <div style={s.headRight}>
          {lead.utm_source && <span style={s.origenBadge}>{lead.utm_source}</span>}
          <span style={s.fecha}>{fmtFechaLima(fecha)}</span>
        </div>
      </div>
      )}

      {expanded && (
        <div style={s.detail}>
          <div style={s.checksRow}>
            <Check ok={lead.distrito_ok} label="Distrito" />
            <Check ok={lead.presupuesto_ok} label="Presupuesto" />
            <Check ok={lead.tiempo_ok} label="Tiempo" />
          </div>

          <div style={s.detailGrid}>
            <section style={s.section}>
              <h4 style={s.sectionTitle}>Contacto</h4>
              <Row label="Nombre" value={lead.nombre} />
              <Row label="WhatsApp" value={lead.whatsapp} />
              <Row label="Email" value={lead.email} />
              <Row label="DNI" value={lead.dni} />
              <Row label="Dirección" value={lead.direccion} />
              <Row label="Distrito" value={lead.distrito} />
            </section>

            <section style={s.section}>
              <h4 style={s.sectionTitle}>Proyecto</h4>
              <Row label="Tipo de espacio" value={lead.tipo_espacio} />
              <Row label="Estado del inmueble" value={lead.estado_inmueble} />
              <Row label="Metros cuadrados" value={lead.metros_cuadrados != null ? `${lead.metros_cuadrados} m²` : ''} />
              {Array.isArray(lead.ambientes) && lead.ambientes.length > 0 && (
                <div style={s.detailRow}>
                  <span style={s.detailLabel}>Ambientes</span>
                  <span style={s.ambientesWrap}>
                    {lead.ambientes.map((a, i) => (
                      <span key={`${a}-${i}`} style={s.ambientePill}>{a}</span>
                    ))}
                  </span>
                </div>
              )}
              <Row label="Otro ambiente" value={lead.ambientes_otro} />
              <Row label="Servicio" value={lead.servicio} />
              <Row label="Presupuesto" value={lead.presupuesto} />
              <Row label="Cuándo inicia" value={lead.cuando_inicia} />
            </section>

            <section style={s.section}>
              <h4 style={s.sectionTitle}>Atribución</h4>
              <Row label="utm_source" value={lead.utm_source} />
              <Row label="utm_medium" value={lead.utm_medium} />
              <Row label="utm_campaign" value={lead.utm_campaign} />
              <Row label="utm_content" value={lead.utm_content} />
              <Row label="utm_term" value={lead.utm_term} />
              {!lead.utm_source && !lead.utm_medium && !lead.utm_campaign
                && !lead.utm_content && !lead.utm_term && (
                <div style={s.sectionEmpty}>Sin datos de campaña.</div>
              )}
            </section>

            {extras.length > 0 && (
              <section style={s.section}>
                <h4 style={s.sectionTitle}>Respuestas adicionales</h4>
                {extras.map(([k, v]) => (
                  <Row key={k} label={k.replace(/[_-]+/g, ' ')} value={v} />
                ))}
              </section>
            )}
          </div>

          {textOf(lead.dudas) && (
            <section style={{ ...s.section, marginTop: 4 }}>
              <h4 style={s.sectionTitle}>Dudas</h4>
              <p style={isMobile ? { ...s.dudas, ...s.dudasMobile } : s.dudas}>{lead.dudas}</p>
            </section>
          )}

          <section style={{ ...s.section, marginTop: 4 }}>
            <h4 style={s.sectionTitle}>Notas internas</h4>
            <textarea
              style={isMobile ? { ...s.notas, ...s.notasMobile } : s.notas}
              rows={3}
              placeholder="Notas del seguimiento — se guardan al salir del campo"
              value={notasValue}
              onChange={e => onNotasChange(e.target.value)}
              onBlur={onNotasBlur}
            />
          </section>

          <div style={s.actions}>
            {wa ? (
              <a style={{ ...s.actionBtn, ...s.waBtn, ...(isCompact ? s.actionBtnTouch : {}) }} href={wa} target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
            ) : (
              <span style={{ ...s.actionBtn, ...s.disabledBtn, ...(isCompact ? s.actionBtnTouch : {}) }}>Sin WhatsApp</span>
            )}
            <button
              type="button"
              style={{ ...s.actionBtn, ...(lead.atendido ? s.atendidoOn : s.atendidoOff), ...(isCompact ? s.actionBtnTouch : {}) }}
              onClick={onToggleAtendido}
            >
              {lead.atendido ? '✓ Atendido' : 'Marcar atendido'}
            </button>
            <button type="button" style={{ ...s.actionBtn, ...s.projectBtn, ...(isCompact ? s.actionBtnTouch : {}) }} onClick={onCrearProyecto}>
              Crear proyecto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile card: one "label: value" line, skipped when empty.
function MobilePair({ label, value }) {
  const text = textOf(value);
  if (!text || text === '—') return null;
  return (
    <div style={s.mPair}>
      <span style={s.mPairLabel}>{label}</span>
      <span style={s.mPairValue}>{text}</span>
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────────
const s = {
  page: { padding: '32px 36px', fontFamily: "'DM Sans', sans-serif" },
  headerRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 16, marginBottom: 20, flexWrap: 'wrap',
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600,
    color: 'var(--wine)', margin: 0,
  },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: 'var(--text-light)' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  updatedAt: { fontSize: 11, color: 'var(--text-light)' },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', borderRadius: 9, border: '1px solid rgba(107,28,42,0.22)',
    background: '#fff', color: 'var(--wine)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  loading: { padding: 40, textAlign: 'center', color: 'var(--text-light)', fontSize: 14 },
  errorBox: {
    padding: '12px 16px', borderRadius: 10, marginBottom: 16,
    background: 'rgba(184,59,59,0.08)', color: '#B83B3B', fontSize: 13,
  },
  errorHint: { marginTop: 4, fontSize: 12, opacity: 0.85 },

  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12, marginBottom: 16,
  },
  kpiCard: {
    background: '#fff', borderRadius: 14, padding: '16px 18px',
    border: '1px solid var(--border)', boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  kpiLabel: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.09em', color: 'var(--text-light)',
  },
  kpiValue: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600,
    color: 'var(--text-primary)', lineHeight: 1.1,
  },
  kpiHint: { fontSize: 11, color: 'var(--text-light)' },

  filterCard: {
    background: '#fff', borderRadius: 14, padding: 16, marginBottom: 16,
    border: '1px solid var(--border)', boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
  },
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '7px 14px', borderRadius: 999, border: '1px solid var(--border)',
    background: '#fff', color: 'var(--text-secondary)', fontSize: 12.5,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  pillCount: {
    fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
    background: 'var(--beige)', color: 'var(--text-secondary)',
  },
  pillCountActive: { background: 'rgba(255,255,255,0.24)', color: '#fff' },

  filterGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 10,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  fieldLabel: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.07em', color: 'var(--text-light)',
  },
  select: {
    width: '100%', padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--border)', fontSize: 12.5,
    fontFamily: "'DM Sans', sans-serif", background: '#fff',
    color: 'var(--text-primary)', boxSizing: 'border-box',
  },
  filterFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, marginTop: 14, flexWrap: 'wrap',
  },
  resultCount: { fontSize: 12, color: 'var(--text-light)' },
  clearBtn: {
    padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)',
    background: '#fff', color: 'var(--text-secondary)', fontSize: 12,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },

  empty: {
    padding: 40, textAlign: 'center', color: 'var(--text-light)',
    fontSize: 13, fontStyle: 'italic',
    background: '#fff', borderRadius: 14, border: '1px solid var(--border)',
  },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: '#fff', borderRadius: 14, border: '1px solid var(--border)',
    boxShadow: '0 3px 14px rgba(0,0,0,0.04)', overflow: 'hidden',
  },
  cardExpanded: { borderColor: 'rgba(107,28,42,0.28)' },
  cardHead: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
    cursor: 'pointer', minWidth: 0,
  },
  chevron: { color: 'var(--wine)', display: 'inline-flex', flexShrink: 0 },
  headMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  headTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  leadName: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  badge: {
    fontSize: 9.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
    letterSpacing: '0.06em', whiteSpace: 'nowrap',
  },
  atendidoBadge: {
    fontSize: 9.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
    background: 'rgba(74,124,89,0.12)', color: '#4A7C59', whiteSpace: 'nowrap',
  },
  headMeta: { display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: 'var(--text-secondary)' },
  metaItem: { whiteSpace: 'nowrap' },
  headRight: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    gap: 4, flexShrink: 0,
  },
  origenBadge: {
    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
    background: 'var(--beige)', color: 'var(--wine)', whiteSpace: 'nowrap',
  },
  fecha: { fontSize: 11, color: 'var(--text-light)', whiteSpace: 'nowrap' },

  detail: {
    borderTop: '1px solid var(--border)', background: 'var(--beige)',
    padding: '14px 16px',
  },
  checksRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  check: {
    fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  detailGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14,
  },
  section: { minWidth: 0 },
  sectionTitle: {
    margin: '0 0 7px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.1em', color: 'var(--wine)',
  },
  sectionEmpty: { fontSize: 12, fontStyle: 'italic', color: 'var(--text-light)' },
  detailRow: {
    display: 'flex', gap: 8, marginBottom: 5, fontSize: 12.5,
    alignItems: 'baseline', minWidth: 0,
  },
  detailLabel: {
    color: 'var(--text-light)', minWidth: 92, flexShrink: 0,
    textTransform: 'capitalize',
  },
  detailValue: { color: 'var(--text-primary)', wordBreak: 'break-word', minWidth: 0 },
  ambientesWrap: { display: 'flex', flexWrap: 'wrap', gap: 5, minWidth: 0 },
  ambientePill: {
    fontSize: 10.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
    background: 'rgba(107,28,42,0.08)', color: 'var(--wine)',
  },
  dudas: {
    margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-primary)',
    background: '#fff', border: '1px solid var(--border)', borderRadius: 9,
    padding: '9px 12px', whiteSpace: 'pre-wrap',
  },
  notas: {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 9,
    border: '1px solid var(--border)', fontSize: 12.5, lineHeight: 1.5,
    fontFamily: "'DM Sans', sans-serif", color: 'var(--text-primary)',
    background: '#fff', resize: 'vertical',
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  actionBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', borderRadius: 9, border: 'none',
    fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", textDecoration: 'none',
  },
  waBtn: { background: '#25D366', color: '#fff' },
  atendidoOn: { background: 'rgba(74,124,89,0.14)', color: '#4A7C59' },
  atendidoOff: { background: '#fff', color: 'var(--text-secondary)', border: '1px solid var(--border)' },
  projectBtn: { background: 'var(--wine)', color: '#fff' },
  disabledBtn: { background: 'var(--beige-dark)', color: 'var(--text-light)', cursor: 'default' },

  // ── compact-only additions (merged only when useBreakpoint() says so) ──
  tapMobile: { minHeight: TAP_TARGET, boxSizing: 'border-box' },
  inputMobile: { minHeight: TAP_TARGET, fontSize: 16 },
  pillRowMobile: {
    flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
    margin: '0 -16px 12px', padding: '0 16px 2px',
  },
  pillMobile: { flexShrink: 0, fontSize: 14 },
  searchRowMobile: { display: 'flex', gap: 8, alignItems: 'stretch' },
  filtersBtnMobile: { flexShrink: 0, fontSize: 14, color: 'var(--wine)', padding: '0 16px' },
  filtersBtnActive: { background: 'var(--wine)', color: '#fff', borderColor: 'var(--wine)' },
  cardHeadMobile: { flexDirection: 'column', alignItems: 'stretch', gap: 8, padding: '14px 16px' },
  mHeadTop: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  leadNameMobile: { fontSize: 16, minWidth: 0, wordBreak: 'break-word' },
  badgeMobile: { fontSize: 11 },
  mPairs: { display: 'flex', flexDirection: 'column', gap: 4 },
  mPair: { display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 15, minWidth: 0 },
  mPairLabel: { color: 'var(--text-light)', fontSize: 13, minWidth: 92, flexShrink: 0 },
  mPairValue: { color: 'var(--text-primary)', wordBreak: 'break-word', minWidth: 0 },
  detailRowMobile: { fontSize: 15, marginBottom: 7 },
  dudasMobile: { fontSize: 15 },
  notasMobile: { fontSize: 16 },
  actionBtnTouch: {
    minHeight: TAP_TARGET, boxSizing: 'border-box',
    flex: '1 1 auto', justifyContent: 'center', fontSize: 14,
  },
};
